import { useEffect, useState, useRef } from 'react';

/**
 * AudioWaveform Component
 * 
 * Real-time audio waveform visualization synchronized with actual AI voice playback.
 * Uses Web Audio API to analyze frequency data and create realistic, dynamic bars.
 * 
 * Features:
 * - Real-time frequency analysis (not fake CSS animation)
 * - Synchronized with actual audio playback
 * - Smooth transitions and natural movement
 * - Responsive to actual voice characteristics
 */

interface AudioWaveformProps {
  isActive?: boolean;
  className?: string;
  barCount?: number;
  colorFrom?: string;
  colorTo?: string;
  getFrequencyData?: () => Uint8Array; // Function to get real audio data
  maxHeight?: number; // Max height in pixels (default: 32)
}

export default function AudioWaveform({ 
  isActive = false,
  className = '',
  barCount = 9,
  colorFrom = 'rgb(16, 185, 129)', // emerald-500
  colorTo = 'rgb(5, 150, 105)',     // emerald-600
  getFrequencyData,
  maxHeight = 32
}: AudioWaveformProps) {
  
  const [barHeights, setBarHeights] = useState<number[]>(new Array(barCount).fill(0.1));
  const animationFrameRef = useRef<number | null>(null);
  
  // Keep track of current and target heights for smooth interpolation
  const currentHeightsRef = useRef<number[]>(new Array(barCount).fill(0.1));
  const targetHeightsRef = useRef<number[]>(new Array(barCount).fill(0.1));
  
  useEffect(() => {
    if (!isActive || !getFrequencyData) {
      // Reset to idle state
      setBarHeights(new Array(barCount).fill(0.1));
      currentHeightsRef.current = new Array(barCount).fill(0.1);
      targetHeightsRef.current = new Array(barCount).fill(0.1);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }
    
    // Smoothing factor: 0.0 (no smoothing) to 1.0 (max smoothing)
    // Lower = faster response, Higher = smoother but more lag
    const smoothingFactor = 0.75;
    
    // Update bar heights from real audio data with smooth interpolation
    const updateBars = () => {
      const frequencyData = getFrequencyData();
      
      // Convert frequency data (0-255) to heights (0.1-1.0)
      const newTargets = Array.from(frequencyData).map(value => {
        // Normalize: 0-255 -> 0.1-1.0
        const normalized = (value / 255) * 0.9 + 0.1;
        // Add some amplification for low volumes
        return Math.min(1.0, normalized * 3.0);
      });
      
      // Interpolate from current to target with easing
      const smoothedHeights = currentHeightsRef.current.map((current, i) => {
        const target = newTargets[i];
        
        // Linear interpolation with smoothing
        // current + (target - current) * speed
        const speed = 1 - smoothingFactor;
        const next = current + (target - current) * speed;
        
        return next;
      });
      
      currentHeightsRef.current = smoothedHeights;
      targetHeightsRef.current = newTargets;
      setBarHeights(smoothedHeights);
      
      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(updateBars);
    };
    
    // Start animation loop
    updateBars();
    
    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isActive, getFrequencyData, barCount]);
  
  return (
    <div className={`flex items-center justify-center gap-1 ${className}`}>
      {barHeights.map((height, index) => (
        <div
          key={index}
          className="w-1 rounded-full"
          style={{
            background: `linear-gradient(to bottom, ${colorFrom}, ${colorTo})`,
            opacity: isActive ? 1 : 0,
            height: `${maxHeight}px`,
            transform: `scaleY(${isActive ? height : 0.1})`,
            transformOrigin: 'center',
            transition: 'opacity 0.2s ease-out',
            // No CSS transition on transform - smoother with JS interpolation
          }}
        />
      ))}
    </div>
  );
}
